// Portuguese (Portugal, formal) copy for /web-uploader. Same shape as copy.en.ts (checked by copy.test.ts).
// "O envio web" is the site's Portuguese name for the web uploader (pricing and preview-first pages).
// The uploader's own labels are in English, so they're quoted as it shows them («Previews», «Originals»).
// "Fotos", not "fotografias" (copy.test.ts keeps every role word out, and that check would catch it).
import type { WebUploaderCopy } from "./copy.en";

const pt: WebUploaderCopy = {
  seo: {
    title: "Envio web da Photohawk | Mais rápido do que o FTP",
    description:
      "Renovámos o envio web da Photohawk. Envia muitas fotos de uma só vez, suporta envios de mais de 100\u00a0000 fotos e retoma sozinho se a ligação falhar, em todos os planos. Em todos os planos, carrega primeiro pré-visualizações leves para que a sua galeria fique online em minutos, e não em horas.",
  },
  hero: {
    badge: "Novo",
    eyebrow: "Em todos os planos",
    title: "O novo envio web da Photohawk",
    subtitle:
      "Mais rápido do que o FTP, feito para envios de mais de 100\u00a0000 fotos e retoma onde parou.",
    plans: "Em todos os planos, com as pré-visualizações primeiro um grande evento fica online em minutos, e não em horas.",
    ctaPrimary: "Abrir o envio web",
    ctaSecondary: "Ver as novidades",
    screenshotAlt:
      "O envio web a meio de um carregamento. Com as pré-visualizações primeiro (todos os planos), a barra de estado indica que a galeria está online com as pré-visualizações e mostra quantas pessoas a visitam, e a fila à direita mostra as pré-visualizações concluídas e os originais a caminho, com duas fotos compradas a passar à frente.",
    phoneAlt: "O envio web num telemóvel, aberto numa pasta da galeria, com o estado do envio em baixo.",
  },
  previews: {
    heading: "Primeiro as pré-visualizações",
    badge: "Todos os planos",
    subtitle:
      "Em todos os planos: online em minutos, e não em horas. Uma galeria de 10\u00a0000 fotos pode estar online e a vender em cerca de 8 minutos com uma velocidade de envio de 20 Mbps.",
    body: "As pré-visualizações leves são carregadas primeiro, para que a sua galeria possa ficar online e começar a vender, ou a dar downloads gratuitos, enquanto os originais seguem. Se alguém comprar ou descarregar uma foto antes de o original chegar, esse original passa para a frente da fila.",
    steps: [
      { id: "previews", text: "As pré-visualizações leves são carregadas primeiro" },
      { id: "live", text: "A sua galeria fica online" },
      { id: "buy", text: "Alguém compra uma foto" },
      { id: "jump", text: "O original dessa foto passa para a frente da fila" },
      { id: "originals", text: "Seguem-se os originais" },
    ],
    anim: {
      label: "Ilustração animada das pré-visualizações primeiro",
      description:
        "As pré-visualizações leves de seis fotos voam do seu computador para a sua galeria, e a galeria fica online. Alguém toca em Comprar numa foto: o original dessa foto passa para a frente da fila de originais e é enviado primeiro. Seguem-se os restantes originais.",
      computer: "O seu computador",
      gallery: "A sua galeria",
      notLive: "Ainda não online",
      live: "Online",
      buy: "Comprar",
      bought: "Comprada",
      queue: "Originais",
      goesFirst: "Vai primeiro",
    },
    link: "Como funciona o envio com pré-visualização primeiro",
  },
  faster: {
    heading: "Porque é mais rápido do que o FTP",
    subtitle: "As mesmas fotos, enviadas de duas formas.",
    anim: {
      label: "Comparação animada entre uma aplicação de FTP e o envio web",
      description:
        "Duas linhas. Em cima, uma aplicação de FTP envia as fotos 2 de cada vez por predefinição, e no máximo 10. Em baixo, o envio web envia muitas fotos de uma só vez, por faixas que alargam e estreitam conforme a ligação. Em todos os planos, as pré-visualizações leves vão primeiro, por isso a galeria fica online mais cedo. A ligação falha por um momento e o envio retoma sozinho.",
      ftp: "Aplicação de FTP",
      ftpNote: "2 de cada vez por predefinição",
      ftpMax: "no máximo 10",
      web: "Envio web",
      webNote: "Muitas de uma só vez",
      adapts: "Adapta-se à sua largura de banda",
      gallery: "Galeria",
      live: "Online",
      dropped: "A ligação falhou",
      resumed: "Retomou sozinho",
      legendPreviews: "Pré-visualizações",
      legendOriginals: "Originais",
    },
    points: [
      {
        id: "parallel",
        title: "Muitas fotos de uma só vez",
        body: "Envia muitas fotos de uma só vez, muitas mais do que uma aplicação de FTP como o FileZilla (2 de cada vez por predefinição, e no máximo 10).",
      },
      {
        id: "adaptive",
        title: "Aproveita ao máximo a sua ligação",
        body: "Vai-se ajustando durante o envio, para aproveitar ao máximo a sua ligação.",
      },
      {
        id: "scale",
        title: "Feito para grandes eventos",
        body: "Testámo-lo em grande escala e suporta sem dificuldade envios de mais de 100\u00a0000 fotos.",
      },
      {
        id: "live",
        title: "Online em minutos, e não em horas (todos os planos)",
        body: "Em todos os planos, as pré-visualizações leves são carregadas primeiro, para que uma galeria de 10\u00a0000 fotos possa estar online e a vender em cerca de 8 minutos com uma velocidade de envio de 20 Mbps, enquanto os originais seguem.",
      },
      {
        id: "resume",
        title: "Retoma onde parou",
        body: "Se a ligação falhar, retoma sozinho. Adicione a pasta novamente e só é enviado o que falta.",
      },
    ],
  },
  estimate: {
    heading: "Quanto tempo vai demorar o seu envio?",
    subtitle: "Uma estimativa para a sua galeria, a partir da sua própria velocidade de envio.",
    heroLink: "Calcule o tempo do seu envio",
    photos: "Número de fotos",
    custom: "Outro",
    customLabel: "Número de fotos (outro)",
    size: "Tamanho médio de um original",
    sizeUnit: "MB",
    sizeHint: "Os JPEG de eventos rondam muitas vezes os {mb} MB.",
    speed: "A sua velocidade de envio",
    speedUnit: "Mbps",
    test: "Testar a minha velocidade de envio",
    testing: "A testar… {mbps} Mbps",
    testStarting: "A testar…",
    testDone: "Medido: {mbps} Mbps de envio",
    testFailed: "Não foi possível medir a sua velocidade agora. Introduza-a manualmente ou experimente um teste de velocidade geral.",
    dataNote: "Usa até cerca de {mb} MB de dados, e só quando toca no botão.",
    otherTest: "Ou use um teste de velocidade geral",
    resultsHeading: "A sua estimativa",
    previewsLabel: "Pré-visualizações primeiro",
    previewsPlans: "Todos os planos",
    previewsLive: "Galeria online em {time}",
    previewsAll: "Todos os originais enviados em {time}",
    originalsLabel: "Só originais",
    originalsLive: "Galeria online em {time}",
    originalsWhen: "quando os {gb} GB de originais estiverem enviados",
    computerNote: "A esta velocidade, quem dita o ritmo é a rapidez com que o seu computador cria as pré-visualizações.",
    disclaimer: "São apenas estimativas. Os tempos reais dependem da sua ligação, do seu computador e das suas fotos.",
    time: {
      under: "menos de um minuto",
      min: "cerca de {n} min",
      h: "cerca de {n} h",
      d: "cerca de {n} dias",
    },
    table: {
      caption: "Exemplo: 10\u00a0000 fotos",
      speed: "Velocidade de envio",
      live: "Pré-visualizações primeiro: galeria online (todos os planos)",
      all: "Todos os originais enviados",
      note: "O valor de cerca de 8 minutos foi medido com uma velocidade de envio de 20 Mbps (pré-visualizações primeiro). As restantes linhas são estimativas do mesmo modelo, com originais de cerca de {mb} MB.",
    },
  },
  features: {
    heading: "Mais novidades",
    subtitle: "Ecrãs reais do novo envio web.",
    items: [
      {
        id: "status",
        title: "O estado, em palavras simples",
        body: "Veja num relance em que ponto está. Com as pré-visualizações primeiro (todos os planos): «Previews» (pré-visualizações) e depois «Originals» (originais), cada um com o seu progresso.",
        alt: "O estado do envio com as pré-visualizações primeiro (todos os planos): «Live — saving originals…», uma linha de progresso para Previews e outra para Originals, e «2 photos people bought go first».",
      },
      {
        id: "queue",
        title: "Uma fila onde lhe for mais prático",
        body: "Coloque a fila de envio à direita, à esquerda ou em baixo, e arraste-a para o tamanho que preferir. O seu navegador memoriza a escolha.",
        alt: "A fila de envio encaixada em baixo, com o menu aberto para a mover para a direita, para baixo ou para a esquerda.",
      },
      {
        id: "visitors",
        title: "Veja quem está a ver",
        body: "A barra de estado mostra quantas pessoas estão a ver a sua galeria neste momento, em todos os planos.",
        alt: "A barra de estado no topo do envio web: «Live on previews» (com as pré-visualizações primeiro) e o número de visitantes.",
      },
      {
        id: "resume",
        title: "Retoma onde parou",
        body: "Se a ligação falhar, retoma sozinho. Adicione a pasta novamente e só é enviado o que falta.",
        alt: "Um aviso no envio web a indicar que uma pasta não terminou de ser enviada, com um botão Resume.",
      },
      {
        id: "phone",
        title: "Claro também no telemóvel",
        body: "Num ecrã pequeno, o caminho das pastas encolhe para caber e, ao tocar em «…», vê as pastas intermédias. Assim, sabe sempre para onde vão as suas fotos.",
        alt: "O envio web num telemóvel: o caminho mostra a pasta atual e «…» abre a lista das pastas acima.",
      },
    ],
  },
  ftp: {
    date: "1 de novembro de 2026",
    heading: "O que muda no FTP a 1 de novembro",
    body: "Os nossos servidores FTP partilhados sempre ativos encerram a 1 de novembro de 2026. Utiliza FTP atualmente? Mude antes dessa data.",
    points: [
      {
        id: "closes",
        title: "O FTP partilhado encerra a 1 de novembro de 2026",
        body: "Os servidores FTP partilhados sempre ativos são desligados a 1 de novembro de 2026.",
      },
      {
        id: "standard",
        title: "O envio web é a forma padrão de enviar fotos",
        body: "Está disponível em todos os planos e pode utilizá-lo desde já.",
      },
      {
        id: "ondemand",
        title: "Se o FTP for indispensável",
        body: "Em todos os planos, qualquer pessoa do seu espaço de trabalho pode iniciar o seu próprio servidor FTP a pedido a partir do envio web, com um endereço pessoal que nunca muda. Desliga-se após uma hora sem atividade.",
      },
    ],
    helpLink: "Como funciona o FTP a pedido",
  },
  more: {
    previewFirstTitle: "Como funciona o envio com pré-visualização primeiro",
    previewFirstBody: "Em todos os planos. Passo a passo: as pré-visualizações, a publicação e o que os compradores veem.",
  },
  cta: {
    heading: "Experimente no seu próximo envio",
    body: "O novo envio web está pronto em todos os planos. Adicione uma pasta e veja a sua galeria ficar online.",
    button: "Abrir o envio web",
  },
  pause: "Pausar a animação",
  play: "Reproduzir a animação",
};

export default pt;
